import React, { useState } from 'react'
import {
  AllUsers_users_users,
  UsersFilterInput,
  useUsers,
  useInviteUser,
  CompanyMemberRole,
  GroupMemberRole,
  useAuth,
  useDebounce,
  UserRole,
  useUserForm,
} from '@modmd/data'
import { NewUser } from 'sharedComponents/NewUser'
import { ConfirmationDialog } from 'components/Dialog'
import TextInput from 'components/TextInput'
import Button from 'components/Button'
import { Box } from 'components/Layout'
import { useToaster } from 'utils/useToaster'
import { StaffMembersSelector } from 'sharedComponents/StaffMembersSelector/StaffMembersSelector'

export interface Props {
  label?: string
  filter?: UsersFilterInput
  onUserSelect: (user: Omit<AllUsers_users_users, '__typename'>) => void
  isInvitationEnabled?: boolean
  isLoadingAddMember?: boolean
  hasBottomContent?: boolean
  disabledEditEmail?: boolean
  errorMessage?: string
  placeholder?: string
  buttonLabel?: string
  isStaff?: boolean
}

const FindOrInviteUser: React.VFC<Props> = ({
  label,
  onUserSelect,
  isInvitationEnabled = true,
  hasBottomContent = false,
  isLoadingAddMember = false,
  errorMessage,
  placeholder,
  filter,
  disabledEditEmail,
  buttonLabel = 'Add Member',
  isStaff = false,
}) => {
  const { getUserCompaniesWithRole, getUserGroupsWithRole } = useAuth()
  const [blockButton, setBlockButton] = useState(false)
  const [isNewUserDialogOpen, setIsNewUserDialogOpen] = React.useState(false)
  const { getUsers, users } = useUsers()
  const { companies } = getUserCompaniesWithRole({
    roles: [CompanyMemberRole.Owner, CompanyMemberRole.Manager],
  })
  const { groups } = getUserGroupsWithRole({
    roles: [GroupMemberRole.Manager, GroupMemberRole.Coordinator],
  })

  const { handleChange, handleBlur, isValid, values, resetForm } = useUserForm({
    onSubmit: () => {},
    validationSchema: 'idOrEmailNoPasswordOnly',
  })
  const { setToastMessage } = useToaster()
  const filterData = React.useMemo(
    () => ({
      ...filter,
      emailOrNameLike: values.email,
      isDeleted: false,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filter, values.email]
  )

  useDebounce(
    () => {
      getUsers({
        variables: {
          filterData,
          pagination: {
            from: 0,
            length: 1,
          },
          verification: {
            companyIds: companies?.map((company) => company.id),
            groupIds: groups?.map((groups) => groups.id),
          },
        },
      })
      setBlockButton(false)
    },
    500,
    [getUsers, values.email]
  )

  const { userInviteForm: userForm, inviteUserData, insuranceFormNewUser } = useInviteUser({
    validationSchema: 'idOrEmailNoPasswordOnly',
    onUserInvite: (user) => {
      onUserSelect(user)
      setIsNewUserDialogOpen(false)
    },
  })

  const inviteUser = React.useCallback(() => {
    if (isInvitationEnabled) {
      void userForm.setFieldValue('email', values.email)
      void userForm.setFieldValue('roles', [UserRole.PATIENT])
      setIsNewUserDialogOpen(true)
    }
  }, [isInvitationEnabled, userForm, values.email])

  const selectedUser = React.useMemo(
    () => users.data?.users.users.find((user) => String(user.email) === String(values.email)),
    [users.data?.users.users, values.email]
  )

  const inviteOrShowNowFound = React.useCallback(() => {
    if (!selectedUser && !isInvitationEnabled) {
      setToastMessage('User not found', 'danger')
    } else {
      inviteUser()
    }
  }, [inviteUser, isInvitationEnabled, selectedUser, setToastMessage])

  const handleSelectChange = React.useCallback(() => {
    if (selectedUser) {
      onUserSelect(selectedUser)
    } else {
      inviteOrShowNowFound()
    }
    resetForm()
  }, [inviteOrShowNowFound, onUserSelect, resetForm, selectedUser])

  const onDismiss = () => {
    setIsNewUserDialogOpen(false)
    insuranceFormNewUser.resetForm()
  }

  return isStaff ? (
    <StaffMembersSelector onAddMember={onUserSelect} />
  ) : (
    <>
      <TextInput
        name="email"
        value={values.email || ''}
        label={label}
        onChange={(e) => {
          setBlockButton(true)
          void handleChange(e)
        }}
        onBlur={handleBlur}
        errorMessage={errorMessage}
        placeholder={placeholder}
        hasBottomContent={hasBottomContent}
        width="100%"
        type="text"
      />
      <Box display="flex" justifyContent="flex-end" mt=".5rem">
        <Button
          onClick={handleSelectChange}
          size="medium"
          disabled={!isValid || !values.email || blockButton}
          isFetching={isLoadingAddMember}
        >
          {buttonLabel}
        </Button>
      </Box>
      <ConfirmationDialog
        title="Add patient"
        isOpen={isNewUserDialogOpen}
        onDismiss={onDismiss}
        actions={
          <>
            <Button onClick={onDismiss} colorVariant="secondary">
              Cancel
            </Button>
            <Button isFetching={inviteUserData.loading} onClick={() => userForm.handleSubmit()}>
              Save
            </Button>
          </>
        }
      >
        <NewUser
          userForm={userForm}
          withInsurance
          insuranceForm={insuranceFormNewUser}
          disabledEditEmail={disabledEditEmail}
          noConfirmationField
        />
      </ConfirmationDialog>
    </>
  )
}

export { FindOrInviteUser }
